import React, { useState, useEffect } from 'react';
import { Users, Search, Edit2, Check, X } from 'lucide-react';
import AdminProtectedRoute from '../AdminProtectedRoute';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import { EmployeeService } from '../../services/employeeService';
import './EmployeesManagement.css';

const EmployeesManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editModeId, setEditModeId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedStatusMentions, setSelectedStatusMentions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const availableRoles = EmployeeService.getAvailableRoles();

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await EmployeeService.getAllEmployees();
      setEmployees(data);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employeeId, field, currentValue) => {
    setEditingId(employeeId);
    setEditingField(field);
    if (field === 'status_mentions') {
      setSelectedStatusMentions(currentValue || []);
    } else if (field === 'roles') {
      setSelectedRoles(currentValue || []);
    } else {
      setEditValue(currentValue || '');
    }
  };

  const handleSave = async () => {
    try {
      if (editingField === 'slack_userid') {
        await EmployeeService.updateEmployeeSlackId(editingId, editValue);
      } else if (editingField === 'status_mentions') {
        await EmployeeService.updateEmployeeStatusMentions(editingId, selectedStatusMentions);
      } else if (editingField === 'roles') {
        await EmployeeService.updateEmployeeRoles(editingId, selectedRoles);
      } else if (editingField === 'phone' || editingField === 'name' || editingField === 'email') {
        await EmployeeService.updateEmployee(editingId, { [editingField]: editValue });
      }
      
      await loadEmployees();
      handleCancel();
    } catch (err) {
      console.error('Error updating employee:', err);
      alert('Failed to update employee: ' + err.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingField(null);
    setEditValue('');
    setSelectedStatusMentions([]);
    setSelectedRoles([]);
    setRoleSearchTerm('');
  };

  const handleEnterEditMode = (employeeId) => {
    setEditModeId(employeeId);
  };

  const handleExitEditMode = () => {
    setEditModeId(null);
    // Also cancel any active field editing
    if (editingId && editingField !== 'roles') {
      handleCancel();
    }
  };

  // Handle clicking outside the roles dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if editing roles and clicked outside the dropdown area
      if (editingField === 'roles') {
        const dropdownContainer = event.target.closest('.status-mentions-editor');
        if (!dropdownContainer) {
          handleCancel();
        }
      }
    };

    if (editingField === 'roles') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingField]);


  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort employees
  const filteredEmployees = employees
    .filter(emp => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        emp.name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.slack_userid?.toLowerCase().includes(search) ||
        emp.roles?.some(role => role.toLowerCase().includes(search))
      );
    })
    .sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

  if (loading) {
    return (
      <AdminProtectedRoute>
        <div className="employees-management-loading">
          <LoadingSpinner message="Loading employees..." />
        </div>
      </AdminProtectedRoute>
    );
  }

  if (error) {
    return (
      <AdminProtectedRoute>
        <div className="employees-management-error">
          <ErrorMessage message={error} onRetry={loadEmployees} />
        </div>
      </AdminProtectedRoute>
    );
  }

  return (
    <AdminProtectedRoute>
      <div className="employees-management">
        <div className="employees-header">
          <div className="employees-title-section">
            <Users size={32} className="employees-icon" />
            <div>
              <h1>Employees Management</h1>
              <p className="employees-subtitle">
                Manage employee roles and status mentions
              </p>
            </div>
          </div>

          <div className="employees-search">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="employees-search-input"
            />
          </div>
        </div>

        <div className="employees-table-container">
          <table className="employees-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Chat ID</th>
                <th onClick={() => handleSort('roles')} className="sortable">
                  Roles {sortConfig.key === 'roles' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(employee => (
                <tr key={employee.id} className={!EmployeeService.isEmployeeActive(employee.status_mentions) ? 'deactivated' : ''}>
                  <td>
                    {editingId === employee.id && editingField === 'name' ? (
                      <div className="edit-field">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-input"
                          placeholder="Enter name"
                          autoFocus
                        />
                        <button onClick={handleSave} className="btn-save" title="Save">
                          <Check size={16} />
                        </button>
                        <button onClick={handleCancel} className="btn-cancel" title="Cancel">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={`editable-field ${editModeId !== employee.id ? 'disabled' : ''}`} onClick={() => editModeId === employee.id && handleEdit(employee.id, 'name', employee.name)}>
                        <span>{employee.name}</span>
                        {editModeId === employee.id && <Edit2 size={14} className="edit-icon" />}
                      </div>
                    )}
                  </td>
                  <td>
                    {editingId === employee.id && editingField === 'email' ? (
                      <div className="edit-field">
                        <input
                          type="email"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-input"
                          placeholder="Enter email"
                          autoFocus
                        />
                        <button onClick={handleSave} className="btn-save" title="Save">
                          <Check size={16} />
                        </button>
                        <button onClick={handleCancel} className="btn-cancel" title="Cancel">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={`editable-field ${editModeId !== employee.id ? 'disabled' : ''}`} onClick={() => editModeId === employee.id && handleEdit(employee.id, 'email', employee.email)}>
                        <span>{employee.email}</span>
                        {editModeId === employee.id && <Edit2 size={14} className="edit-icon" />}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="readonly-field">{employee.abconnect_user_id ? employee.abconnect_user_id.slice(0, 8) + '...' : <span className="placeholder">Not linked</span>}</span>
                  </td>
                  <td>
                    {editingId === employee.id && editingField === 'roles' ? (
                      <div className="status-mentions-editor">
                        <input
                          type="text"
                          placeholder="Search roles..."
                          value={roleSearchTerm}
                          onChange={(e) => setRoleSearchTerm(e.target.value)}
                          className="edit-input"
                          style={{ marginBottom: '8px' }}
                        />
                        <div className="status-mentions-dropdown">
                          {availableRoles
                            .filter(role => role.toLowerCase().includes(roleSearchTerm.toLowerCase()))
                            .map(role => (
                            <label key={role} className="status-mention-option">
                              <input
                                type="checkbox"
                                checked={selectedRoles.includes(role)}
                                onChange={() => toggleRole(role)}
                              />
                              <span>{role}</span>
                            </label>
                          ))}
                        </div>
                        <div className="edit-actions">
                          <button onClick={handleSave} className="btn-save" title="Save">
                            <Check size={16} />
                          </button>
                          <button onClick={handleCancel} className="btn-cancel" title="Cancel">
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="status-mentions-field" onClick={() => handleEdit(employee.id, 'roles', employee.roles)}>
                        {employee.roles && employee.roles.length > 0 ? (
                          <div className="status-mentions-tags">
                            {employee.roles.map(role => (
                              <span key={role} className={`status-tag role-${role.toLowerCase().replace(/\s+/g, '-')}`}>
                                {role}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="placeholder">Click to add</span>
                        )}
                        <Edit2 size={14} className="edit-icon" />
                      </div>
                    )}
                  </td>
                  <td>
                    {editingId === employee.id && editingField === 'phone' ? (
                      <div className="edit-field">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-input"
                          placeholder="Enter phone"
                          autoFocus
                        />
                        <button onClick={handleSave} className="btn-save" title="Save">
                          <Check size={16} />
                        </button>
                        <button onClick={handleCancel} className="btn-cancel" title="Cancel">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={`editable-field ${editModeId !== employee.id ? 'disabled' : ''}`} onClick={() => editModeId === employee.id && handleEdit(employee.id, 'phone', employee.phone)}>
                        <span>{employee.phone || <span className="placeholder">-</span>}</span>
                        {editModeId === employee.id && <Edit2 size={14} className="edit-icon" />}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${EmployeeService.isEmployeeActive(employee.status_mentions) ? 'active' : 'inactive'}`}>
                      {EmployeeService.isEmployeeActive(employee.status_mentions) ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td>
                    <div className="employee-actions">
                      {editModeId === employee.id ? (
                        <>
                          <button 
                            onClick={handleExitEditMode} 
                            className="btn-action btn-save"
                            title="Exit Edit Mode"
                          >
                            <Check size={16} />
                            Done
                          </button>
                          <button 
                            onClick={handleExitEditMode} 
                            className="btn-action btn-cancel"
                            title="Cancel Edit Mode"
                          >
                            <X size={16} />
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button 
                          onClick={() => handleEnterEditMode(employee.id)} 
                          className="btn-action btn-edit"
                          title="Edit Employee"
                        >
                          <Edit2 size={16} />
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminProtectedRoute>
  );
};

export default EmployeesManagement;